import {
    Button,
    Count,
    Dropdown,
    Filter,
    Grid,
    GridColumn,
    IButtonProps,
    IFilterProps,
    IProgressProps,
    ISelectOption,
    ISelectSingleProps,
    isMobileDevice,
    Progress,
    Select,
} from '../../components';
import React, {useContext, useState} from 'react';
import {StoreContext} from '../../../app/src/base/root-store';

export interface IPageTitleAndFiltersProps {
    title: string
    dashboardSwitcher?: IFilterProps;
    actions?: IButtonProps[];
    contentFilters?: IContentFiltersProps;
    showEmailButton?: boolean;
}

export interface IContentFiltersProps {
    filterValues?: IPageFilterValuesProps[] ;
}

export interface IPageFilterValuesProps {
    oneFilterValue?: IPageSelectProps;
}

export interface IPageSelectProps extends Omit<ISelectSingleProps, 'options'> {
    options: IPageOptionProps[];
}

export interface IPageOptionProps extends ISelectOption {
    progress?: IProgressProps;
    count?: number;
}

const PageTitleAndFilters = (props: IPageTitleAndFiltersProps): JSX.Element => {
    const [isOpened, setIsOpened] = useState<boolean>(false);
    const store = useContext(StoreContext).teamResultsStore;
    const labelStore = useContext(StoreContext).labelStore;
    const userStore = useContext(StoreContext).userStore;

    const renderContentFiltersExpandButton = <Button
        text={''}
        style={'square'}
        color={'white'}
        icon={isOpened ? 'arrow-up' : 'arrow-down'}
        onClick={(event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>): void => {
            event.preventDefault();
            if (isOpened) {
                setIsOpened(false);
            } else {
                setIsOpened(true);
            }
        }}
    />;

    const renderBaseContentFilters = (filters: IContentFiltersProps): JSX.Element[] => {

        const baseContentFiltersRow: JSX.Element[] = [];

        if (filters.filterValues !== undefined && filters.filterValues.length > 0) {
            const baseFilters = filters.filterValues
                .filter((value) => value.oneFilterValue !== undefined)
                .slice(0, filters.filterValues.length > 5 ? 5 : filters.filterValues.length);

            baseContentFiltersRow.push((
                <Grid noWrap={['md']} key={'base_filter_grid'}>
                    {baseFilters && baseFilters.map((value, index) => (
                        value.oneFilterValue &&
                            <GridColumn width={['xs-12', 'md-3']} key={`base_filter_${index}`}>
                                <Select {...value.oneFilterValue} options={getFilterOptions(value.oneFilterValue.options)} style="borderless"/>
                            </GridColumn>
                    ),
                    )}
                </Grid>));
        }

        return baseContentFiltersRow;
    };

    const renderAdditionalContentFilters = (filters: IContentFiltersProps): JSX.Element[] => {

        const additionalFiltersRows: JSX.Element[] = [];
        const additionalFiltersBlock: JSX.Element[] = [];

        if (filters.filterValues !== undefined && filters.filterValues.filter((value) => value.oneFilterValue !== undefined).length > 5) {

            const availableFilters = filters.filterValues.filter((value) => value.oneFilterValue !== undefined);
            let additionalFilters = availableFilters;

            for (let i = 5; i <= availableFilters.filter((value) => value.oneFilterValue !== undefined).length; i += 6) {
                additionalFilters = availableFilters.slice(i, i + 6);

                additionalFiltersRows.push((
                    <Grid noWrap={['md']} key={`additional_filters_grid_${i}`}>
                        {additionalFilters && additionalFilters.map((filter, index) => (
                            filter.oneFilterValue &&
                                <GridColumn width={['xs-12', 'md-2', 'lg-2']} key={`additional_filters_gridcol_${index}_${i}`}>
                                    <Select {...filter.oneFilterValue} options={getFilterOptions(filter.oneFilterValue.options)} style="borderless"/>
                                </GridColumn>
                        ),
                        )}
                    </Grid>));
            }
        }

        for (const item of additionalFiltersRows) {
            additionalFiltersBlock.push(
                <Grid noWrap={['md']} align={['middle-xs']} justify={['between-xs', 'start-lg']} key={`additional_filters_block_grid_${item.key}`}>
                    <GridColumn width={['xs-12']} key={`additional_filters_block_gridcol_${item.key}`}>
                        {item}
                    </GridColumn>
                </Grid>,
            );
        }

        return additionalFiltersBlock;
    };

    const getFilterOptions: (options: IPageOptionProps[]) => ISelectOption[] = (options: IPageOptionProps[]) => (
        options.map((el: IPageOptionProps) => {
            const labelClassName = el.disabled ? 'disabled' : undefined;
            const countClassName = el.disabled ? 'grey' : 'green';

            return ({
                ...el,
                children: el.progress || el.count ? (
                    <Grid align={['middle-xs']} noWrap={['xs']} verticalGutter='none'>
                        <GridColumn width={['max-xs']}>
                            <span className={labelClassName}>{el.label}</span>
                            {el.count &&
                                <Count
                                    className="select__count"
                                    color={countClassName}
                                    number={el.count}
                                    size={'sm'}
                                />
                            }
                        </GridColumn>
                        {el.progress && <GridColumn width={['min-xs']}>
                            <Progress {...el.progress} />
                        </GridColumn>}

                    </Grid>
                ) : undefined,
            });
        })
    );

    const renderDropdownActions: (actions: IButtonProps[]) => JSX.Element = (actions: IButtonProps[]) => (<Dropdown
        placement="bottom"
        triggerType="click"
    >
        <div className="text">
            {actions.map((action: IButtonProps, i: number) => (
                <div key={'history-action-' + i}>
                    <Button {...action} style="link"/>
                </div>
            ))}
        </div>
    </Dropdown>
    );

    return (<>
        <Grid noWrap={['lg']} justify={['between-xs']} align={['middle-xs']}>
            <GridColumn width={['max-xs']}>
                <Grid noWrap={['sm']} align={['middle-xs']} justify={['between-xs', 'start-md']}>
                    <GridColumn width={['min-xs']}>
                        <div style={{display: 'flex', alignItems: 'right'}}><h2>{props.title}</h2>
                            {isMobileDevice() && props.showEmailButton &&
                                <Button
                                    onClick={() => store.openSendResultsHelpModal()}
                                    iconRight={'mail-out'}
                                    className="button--style-link"/>}
                        </div>
                    </GridColumn>
                    {props.dashboardSwitcher &&
                        <GridColumn className="h-hidden-xs" align={['middle-xs']} width={['max-xs']}>
                            <Filter {...props.dashboardSwitcher} hideLabel={true} />
                        </GridColumn>
                    }
                    { (userStore.payload?.isAdmin || userStore.payload?.hasManagerRights) && (props.dashboardSwitcher?.value !== 'my-results') && userStore.payload?.isMentorEnabled === true && (
                        <GridColumn width={['min-xs']}>
                            <Button
                                className={'custom-summary-button'}
                                text={labelStore.label('dashboard.mentor.summary')}
                                style={'filter'}
                                onClick={async () => await store.routeToMentorForSummary('dashboard')}
                                iconRight={'robot-happy'}
                                color={'white'}
                            />
                        </GridColumn>
                    )}
                    <GridColumn className="h-hidden-md h-hidden-lg h-hidden-xl" width={['min-xs']} align={['middle-xs']}>
                        <Grid>
                            {props.actions &&
                                        <GridColumn width={['min-xs']}>
                                            {renderDropdownActions(props.actions)}
                                        </GridColumn>
                            }
                            {
                                props.contentFilters && props.contentFilters.filterValues && props.contentFilters.filterValues.filter((value) => value.oneFilterValue !== undefined).length > 5 &&
                                        <GridColumn width={['min-xs']}>
                                            {renderContentFiltersExpandButton}
                                        </GridColumn>
                            }
                        </Grid>
                    </GridColumn>
                </Grid>
            </GridColumn>
        </Grid>
        <Grid noWrap={['md']} align={['middle-xs']} justify={['between-xs', 'between-lg']}>
            {props.dashboardSwitcher &&
                <GridColumn align={['middle-xs']} className="h-hidden-sm h-hidden-md h-hidden-lg h-hidden-xl" width={['min-xs']}>
                    <Filter {...props.dashboardSwitcher} hideLabel={true}/>
                </GridColumn>
            }
            {props.contentFilters &&
                        <GridColumn width={['xs-12', 'md-8']}>
                            {props.contentFilters && renderBaseContentFilters(props.contentFilters)}
                        </GridColumn>
            }
            <GridColumn className="h-hidden-xs h-hidden-sm" width={['min-xs']}>
                <Grid>
                    {props.actions &&
                                <GridColumn width={['min-xs']}>
                                    {renderDropdownActions(props.actions)}
                                </GridColumn>
                    }
                    {
                        props.contentFilters && props.contentFilters.filterValues && props.contentFilters.filterValues.filter((value) => value.oneFilterValue !== undefined).length > 5 &&
                                <GridColumn width={['min-xs']}>
                                    {renderContentFiltersExpandButton}
                                </GridColumn>
                    }
                </Grid>
            </GridColumn>
        </Grid>
        {props.contentFilters && isOpened && renderAdditionalContentFilters(props.contentFilters)}
    </>
    );
};

export default PageTitleAndFilters;
